import { ArrowUpRight } from "lucide-react";
import { DandelionSeed } from "@/components/DandelionSeed";

const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_therapy-warm/artifacts/utl881rv_WhatsApp%20Image%202026-06-14%20at%2012.49.38.jpeg";

export default function AboutPreview() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="ueber-mich"
      data-testid="about-section"
      className="relative py-24 lg:py-32"
      style={{ background: "var(--cream-deep)" }}
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 reveal">
          <div className="relative">
            <div
              className="absolute -top-6 -left-6 w-32 h-32 rounded-full opacity-80"
              style={{ background: "var(--sky)" }}
              aria-hidden="true"
            />
            <div
              className="absolute -bottom-6 -right-4 w-40 h-40 rounded-full opacity-60"
              style={{ background: "var(--beige)" }}
              aria-hidden="true"
            />
            <div className="image-frame relative aspect-[4/5]">
              <img
                src={PORTRAIT}
                alt="Petra Fellmann — Heilpraktikerin für Psychotherapie"
                className="w-full h-full object-cover"
                data-testid="about-portrait"
                loading="lazy"
              />
            </div>
          </div>

          {/* Zitat-Karte separat unterhalb des Bildes */}
          <div
            className="mt-10 bg-white rounded-2xl px-6 py-5 border shadow-lg flex items-start gap-3"
            style={{ borderColor: "var(--line)" }}
            data-testid="about-quote"
          >
            <DandelionSeed size={22} className="shrink-0 mt-1" />
            <div>
              <p
                className="font-serif italic text-[14px] sm:text-[15px] leading-relaxed"
                style={{ color: "var(--ink)" }}
              >
                {`„Ich glaube daran, dass das größte Geschenk, das ich von jemandem empfangen kann, ist, gesehen, gehört, verstanden und berührt zu werden. Das größte Geschenk, das ich geben kann, ist, den anderen zu sehen, zu hören, zu verstehen und zu berühren. Wenn dies geschieht, entsteht Kontakt."`}
              </p>
              <p
                className="mt-3 text-[10px] tracking-[0.24em] uppercase"
                style={{ color: "var(--ink-mute)" }}
              >
                — Virginia Satir
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <p className="eyebrow reveal">Über mich</p>
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 reveal reveal-delay-1"
            style={{ color: "var(--ink)" }}
          >
            Ich begleite Sie —{" "}
            <em className="font-serif italic" style={{ color: "var(--sky-deep)" }}>
              achtsam &amp; feinfühlig
            </em>
            .
          </h2>

          <div
            className="mt-7 space-y-4 leading-relaxed reveal reveal-delay-2"
            style={{ color: "var(--ink-soft)" }}
            data-testid="about-text"
          >
            <p>
              Wir gehen in Ihrem Tempo, ohne Druck — denn Veränderung und Entwicklung
              brauchen Zeit.
            </p>
            <p>
              Wir wollen nicht an alten Wunden rühren, sondern im Hier und Jetzt vorwärtsgewandt
              den Raum für neue Möglichkeiten öffnen. Sie finden hier einen bewertungsfreien
              Entfaltungsraum für ein neues Bewusstsein und Erleben Ihrer Welt.
            </p>
            <p>
              Die Vielfältigkeit der Menschen hat mich schon immer fasziniert — deshalb ist es
              mir ein großes Anliegen, Sie unabhängig von Herkunft, Religion, Leistungsfähigkeit
              oder Beeinträchtigung als einzigartig und wertvoll zu betrachten.
            </p>
            <p>
              Als <strong style={{ color: "var(--ink)" }}>Heilpraktikerin beschränkt auf das
              Gebiet der Psychotherapie</strong>, <strong style={{ color: "var(--ink)" }}>
              diplomierte Krankenschwester für psychiatrische Krankenpflege</strong> und{" "}
              <strong style={{ color: "var(--ink)" }}>systemische Traumatherapeutin</strong>{" "}
              bringe ich einen reichen Erfahrungsschatz mit. Seit über 30 Jahren arbeite ich mit
              Menschen in schwierigen Lebenslagen.
            </p>
            <p>
              Diese Erfahrungen haben mein Verständnis und meine Liebe zu den Menschen — und die
              Freude an meinem Beruf — wachsen lassen.
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-2 reveal reveal-delay-3">
            {[
              "Heilpraktikerin Psychotherapie",
              "Systemische Traumatherapeutin",
              "Diplomierte Krankenschwester (Psychiatrie)",
              "30+ Jahre Berufserfahrung",
              "VFP-Mitglied",
            ].map((q) => (
              <span
                key={q}
                data-testid={`qualification-${q.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="text-xs px-3 py-1.5 rounded-full border"
                style={{ borderColor: "var(--line)", background: "white", color: "var(--ink)" }}
              >
                {q}
              </span>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4 reveal reveal-delay-4">
            <button
              data-testid="about-cta-more"
              onClick={() => scrollTo("#kontakt")}
              className="btn-primary"
            >
              Kennenlerngespräch vereinbaren
              <ArrowUpRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
